import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CONVERSATION_LANGUAGES, ConversationLanguage } from "./conversation-adapter";

export const APP_LANGUAGE_STORAGE_KEY = "superformula-demo-language-v1";

const JA = {
  "language.selector": "表示と言語",
  "conversation.micOff": "マイクはオフです",
  "conversation.waiting": "マイクはONです。話しかけてください。",
  "conversation.contextSync": "会話の走行文脈を同期中です",
  "conversation.vadLoading": "VADモデルをローカルで準備中です",
  "conversation.vadUnavailable": "VADを利用できません",
  "conversation.recognizing": "発話を認識中です",
  "conversation.transcribing": "発話を文字起こししています",
  "conversation.thinking": "車両AIが回答を考えています",
  "conversation.preparing": "車両AIの音声を準備しています",
  "conversation.responding": "車両AIが回答しています",
  "conversation.micOn": "MIC ON",
  "conversation.micOffLabel": "MIC OFF",
  "conversation.autoInput": "自動入力",
  "conversation.autoWaiting": "自動会話モードです。",
  "conversation.clickLogo": "ロゴをクリック",
  "conversation.turnMicOn": "マイクをオンにする",
  "conversation.turnMicOff": "マイクをオフにする",
  "conversation.turnAutoInput": "自動入力に切り替える",
  "conversation.venueNoise": "周囲の音声を多く拾っています。ロゴを押している間だけ話してください。静かな状態が続くと通常入力に戻ります。",
  "conversation.pushToTalk": "ロゴを押している間だけ音声を認識します",
  "conversation.pushToTalkLabel": "PUSH TO TALK",
  "conversation.holdLogo": "ロゴを長押し",
  "conversation.returnToNormalMic": "通常の音声入力に戻す",
  "conversation.restartFailed": "マイクを再開できませんでした",
  "conversation.unavailable": "会話だけを利用できません",
  "conversation.audioError": "回答音声の再生中にエラーが発生しました。字幕は利用できます。",
  "conversation.playError": "回答音声を再生できませんでした。字幕は利用できます。",
  "conversation.mobileCaptionNote": "※回答字幕は上の車載カメラ映像に表示されます",
  "conversation.volumeBalance": "iPhone音量バランス",
  "conversation.volumeBalanceMicOn": "MIC ON時のオンボード音量",
  "conversation.volumeBalanceReply": "AI発話の音量",
  "conversation.volumeBalanceHelp": "オンボード音声は左へ、AI音声は右へ調整できます。この端末に保存されます。",
  "shell.onboard": "車載カメラ映像",
  "shell.emotion": "選手と車両の感情地図",
  "shell.course": "鈴鹿コースと走行線",
  "shell.conversation": "車両AIとの音声対話",
  "shell.telemetry": "走行データ",
  "shell.emotionTitle": "感情地図",
  "shell.emotionDescription": "選手を背景、車両を前景として一枚の地図へ重ねる表示領域",
  "shell.courseTitle": "実走行ライン × 生成ライン",
  "shell.courseDescription": "記録GPSと非介入の生成線を同じ周回と時刻で表示する領域",
  "shell.conversationTitle": "音声対話",
  "shell.conversationDescription": "AIロゴを主操作とする音声対話領域",
  "shell.telemetryTitle": "現在値 × 時系列",
  "shell.telemetryDescription": "操舵、アクセル、ブレーキ、ギア、車速、回転数、車両応答の表示領域",
  "transport.controls": "レース再生操作",
  "transport.play": "再生",
  "transport.pause": "一時停止",
  "transport.mute": "音声をミュート",
  "transport.unmute": "音声を再開",
  "transport.car": "車両",
  "transport.raceTime": "レース時刻",
  "transport.seek": "移動",
  "transport.seekLabel": "フォーメーションラップ開始からゴールまでの再生位置",
  "story.title": "走行イベント / 物語",
  "story.current": "現在の走行イベント",
  "emotion.aria": "独立に計算した選手と車両を一枚に重ねた感情地図",
  "emotion.driver": "選手",
  "emotion.vehicle": "車両",
  "emotion.sync": "シンクロ",
  "emotion.loadError": "感情地図を読み込めません",
  "emotion.loading": "感情地図の全時点系列を読み込み中…",
  "emotion.indicators": "選手側と車両側の6指標",
  "emotion.driverIndicators": "選手の指標",
  "emotion.vehicleIndicators": "車両の指標",
  "emotion.syncRate": "シンクロ率",
  "emotion.origin": "状況: 外部由来 / 反応: 内部由来",
  "emotion.tooltipUnavailable": "対訳未設定",
  "emotion.axisPleasant": "快",
  "emotion.axisUnpleasant": "不快",
  "emotion.axisSituation": "状況",
  "emotion.axisReaction": "反応",
  "emotion.cellsMandala": "セル / 放射状曼荼羅",
  "line.actualOnly": "実走行のみ",
  "line.generatedOnly": "予測のみ",
  "line.both": "すべて",
  "line.loading": "読み込み中",
  "line.actual": "実走行（記録GPS）",
  "line.predicted": "予測トラジェクトリ",
  "line.driverIntent": "選手モデルのライン意図",
  "line.vehicleResponse": "車両応答予測",
  "line.toggle": "走行ラインの表示切替",
  "line.aria": "記録GPSの実走行ライン、選手の操舵意図、車両応答予測",
  "telemetry.legend": "グラフの凡例",
  "telemetry.accelerator": "アクセル",
  "telemetry.brake": "ブレーキ",
  "telemetry.steering": "操舵",
  "telemetry.response": "応答",
  "telemetry.longG": "前後G",
  "telemetry.latG": "横G",
  "telemetry.vertG": "上下G",
  "telemetry.chart": "直近10秒の走行データ",
  "telemetry.current": "現在の操作と応答",
  "network.live": "実行状態の観測",
  "network.title": "AzpNet信号ネットワーク",
  "network.raceView": "レース画面",
  "network.loading": "読み込み中",
  "network.pulses": "実際の信号パルス",
  "network.nodes": "ネットワークノード",
  "network.edges": "ネットワークリンク",
  "status.shared": "共有再生状態",
  "status.raceTime": "レース時刻",
  "status.playState": "再生状態",
  "status.selectedCar": "選択車両",
  "status.seekGeneration": "シーク世代",
  "status.playing": "再生中",
  "status.paused": "一時停止中",
  "error.raceLineApi": "走行ラインAPIが応答しません",
  "error.raceLineIdentity": "走行ラインの車両またはシーク世代が一致しません",
  "error.telemetryApi": "走行データAPIが応答しません",
  "error.conversationHttp": "音声対話を利用できません",
  "error.videoSafeStopped": "完了できず、映像を安全に停止しました",
  "error.videoResynchronized": "完了できなかったため、直前の共有再生状態へ再同期しました",
  "error.videoPrepare": "映像を準備できません",
  "error.videoClockMismatch": "共有再生時計は一時停止でしたが映像が進んだため、映像を安全に停止しました",
  "error.invalidRaceTime": "再生時刻は指定範囲内の整数で指定してください",
  "error.videoAction": "映像操作を完了できません",
  "operation.initialSync": "初期映像の同期",
  "operation.seek": "映像のシーク",
  "operation.switchCar": "車両映像の切替",
  "operation.play": "映像の再生",
  "operation.pause": "映像の一時停止",
  "gauge.notReady": "未準備",
  "gauge.recentSeries": "直近2秒 / 実系列",
  "gauge.seriesNotReady": "実系列 未準備",
  "gauge.mainContribution": "主寄与",
  "gauge.noEffect": "作用なし",
  "gauge.noContribution": "生成側からの寄与情報なし",
  "gauge.reserve": "貯蔵量",
  "gauge.daAttenuation": "DA減衰",
  "gauge.naFastAttenuation": "NA高速減衰",
  "gauge.indicators": "疑似指標 / 0–1",
} as const;

export type TranslationKey = keyof typeof JA;
type Catalog = Record<TranslationKey, string>;

const EN: Catalog = {
  ...JA,
  "language.selector": "Display language", "conversation.micOff": "Microphone is off",
  "conversation.waiting": "Ready. Please speak", "conversation.contextSync": "Synchronizing race context",
  "conversation.vadLoading": "Preparing local voice detection", "conversation.vadUnavailable": "Voice detection is unavailable",
  "conversation.recognizing": "Listening", "conversation.transcribing": "Transcribing your speech",
  "conversation.thinking": "The car AI is thinking", "conversation.preparing": "Preparing the car AI voice",
  "conversation.responding": "The car AI is responding", "conversation.clickLogo": "Click the logo",
  "conversation.turnMicOn": "Turn microphone on", "conversation.turnMicOff": "Turn microphone off",
  "conversation.venueNoise": "Too much surrounding speech is being detected. Hold the logo while you speak. Normal input returns after the surroundings stay quiet.",
  "conversation.pushToTalk": "Voice is recognized only while you hold the logo",
  "conversation.pushToTalkLabel": "PUSH TO TALK", "conversation.holdLogo": "Hold the logo",
  "conversation.returnToNormalMic": "Return to normal voice input",
  "conversation.restartFailed": "Could not restart the microphone", "conversation.unavailable": "Conversation is unavailable",
  "conversation.audioError": "Voice playback failed. Captions are still available.",
  "conversation.playError": "Could not play the response. Captions are still available.",
  "conversation.mobileCaptionNote": "* Response captions appear over the onboard video above.",
  "conversation.volumeBalance": "iPhone audio balance",
  "conversation.volumeBalanceMicOn": "Onboard audio while MIC is ON",
  "conversation.volumeBalanceReply": "AI voice volume",
  "conversation.volumeBalanceHelp": "Move onboard audio left or AI voice right. These settings are saved on this device.",
  "shell.onboard": "Onboard camera", "shell.emotion": "Driver and vehicle emotion map",
  "shell.course": "Suzuka course and racing lines", "shell.conversation": "Voice conversation with the car AI",
  "shell.telemetry": "Driving data", "shell.emotionTitle": "Emotion map",
  "shell.emotionDescription": "Driver in the background and vehicle in the foreground on one map",
  "shell.courseTitle": "Recorded line × generated line", "shell.courseDescription": "Recorded GPS and non-intervening generated lines at the same lap and time",
  "shell.conversationTitle": "Voice conversation", "shell.conversationDescription": "Voice interface operated from the AI logo",
  "shell.telemetryTitle": "Current values × timeline", "shell.telemetryDescription": "Steering, throttle, brake, gear, speed, RPM, and vehicle response",
  "transport.controls": "Race playback controls", "transport.play": "PLAY", "transport.pause": "PAUSE",
  "transport.mute": "Mute audio", "transport.unmute": "Unmute audio",
  "transport.car": "CAR", "transport.raceTime": "RACE TIME", "transport.seek": "SEEK",
  "transport.seekLabel": "Playback position from the formation lap to the finish",
  "story.title": "RACE EVENT / STORY", "story.current": "Current race event",
  "emotion.aria": "Independent driver and vehicle emotion states overlaid on one map",
  "emotion.driver": "Driver", "emotion.vehicle": "Vehicle", "emotion.sync": "Sync",
  "emotion.loadError": "Could not load the emotion map", "emotion.loading": "Loading the full emotion-map timeline…",
  "emotion.indicators": "Six driver and vehicle indicators", "emotion.driverIndicators": "Driver indicators",
  "emotion.vehicleIndicators": "Vehicle indicators", "emotion.syncRate": "Synchronization rate",
  "emotion.origin": "Situation: external / Reaction: internal", "emotion.tooltipUnavailable": "Translation not configured",
  "emotion.axisPleasant": "Pleasant", "emotion.axisUnpleasant": "Unpleasant",
  "emotion.axisSituation": "Situation", "emotion.axisReaction": "Reaction",
  "emotion.cellsMandala": "cells / radial mandala",
  "line.actualOnly": "Recorded only", "line.generatedOnly": "Predicted only", "line.both": "All",
  "line.loading": "Loading", "line.actual": "Recorded line (GPS)", "line.predicted": "Predicted trajectory",
  "line.driverIntent": "Driver-model line intent", "line.vehicleResponse": "Vehicle-response prediction",
  "line.toggle": "Racing-line display", "line.aria": "Recorded GPS line, driver intent, and vehicle-response prediction",
  "telemetry.legend": "Chart legend", "telemetry.accelerator": "accelerator", "telemetry.brake": "brake",
  "telemetry.steering": "steering", "telemetry.response": "response", "telemetry.longG": "longitudinal G",
  "telemetry.latG": "lateral G", "telemetry.vertG": "vertical G", "telemetry.chart": "Driving data for the last 10 seconds",
  "telemetry.current": "Current operation and response", "network.live": "LIVE RUNTIME OBSERVATION",
  "network.title": "AzpNet Signal Network", "network.raceView": "RACE VIEW", "network.loading": "loading",
  "network.pulses": "actual signal pulses", "network.nodes": "Network nodes", "network.edges": "Network links",
  "status.shared": "Shared playback state", "status.raceTime": "RACE TIME", "status.playState": "PLAY STATE",
  "status.selectedCar": "SELECTED CAR", "status.seekGeneration": "SEEK GENERATION",
  "status.playing": "PLAYING", "status.paused": "PAUSED",
  "error.raceLineApi": "The racing-line API did not respond", "error.raceLineIdentity": "The racing-line car or seek generation does not match",
  "error.telemetryApi": "The driving-data API did not respond", "error.conversationHttp": "Voice conversation is unavailable",
  "error.videoSafeStopped": "could not be completed; the video was stopped safely",
  "error.videoResynchronized": "could not be completed; playback was resynchronized to the previous shared state",
  "error.videoPrepare": "The video could not be prepared", "error.videoClockMismatch": "The shared clock was paused while the video advanced; the video was stopped safely",
  "error.invalidRaceTime": "The playback time must be an integer within the available range", "error.videoAction": "The video operation could not be completed",
  "operation.initialSync": "Initial video synchronization", "operation.seek": "Video seek", "operation.switchCar": "Onboard-car switch",
  "operation.play": "Video playback", "operation.pause": "Video pause",
  "gauge.notReady": "Not ready", "gauge.recentSeries": "Last 2 seconds / live series", "gauge.seriesNotReady": "Live series not ready",
  "gauge.mainContribution": "Main contribution", "gauge.noEffect": "No effect", "gauge.noContribution": "No generated contribution data",
  "gauge.reserve": "Reserve", "gauge.daAttenuation": "DA attenuation", "gauge.naFastAttenuation": "Fast NA attenuation",
  "gauge.indicators": "Pseudo indicators / 0–1",
};

const ES: Catalog = {
  ...EN,
  "language.selector": "Idioma de la pantalla", "conversation.micOff": "Micrófono apagado",
  "conversation.waiting": "Listo. Puedes hablar", "conversation.contextSync": "Sincronizando el contexto de carrera",
  "conversation.vadLoading": "Preparando la detección de voz local", "conversation.vadUnavailable": "La detección de voz no está disponible",
  "conversation.recognizing": "Escuchando", "conversation.transcribing": "Transcribiendo tu voz",
  "conversation.thinking": "La IA del coche está pensando", "conversation.preparing": "Preparando la voz de la IA",
  "conversation.responding": "La IA del coche está respondiendo", "conversation.clickLogo": "Pulsa el logotipo",
  "conversation.turnMicOn": "Activar el micrófono", "conversation.turnMicOff": "Desactivar el micrófono",
  "conversation.venueNoise": "Se detectan demasiadas voces alrededor. Mantén pulsado el logotipo mientras hablas. La entrada normal vuelve cuando el entorno permanece en silencio.",
  "conversation.pushToTalk": "La voz solo se reconoce mientras mantienes pulsado el logotipo",
  "conversation.pushToTalkLabel": "PULSAR PARA HABLAR", "conversation.holdLogo": "Mantén pulsado el logotipo",
  "conversation.returnToNormalMic": "Volver a la entrada de voz normal",
  "conversation.restartFailed": "No se pudo reactivar el micrófono", "conversation.unavailable": "La conversación no está disponible",
  "conversation.audioError": "Falló la reproducción. Los subtítulos siguen disponibles.",
  "conversation.playError": "No se pudo reproducir la respuesta. Los subtítulos siguen disponibles.",
  "conversation.mobileCaptionNote": "* Los subtítulos de la respuesta aparecen sobre el vídeo a bordo de arriba.",
  "conversation.volumeBalance": "Balance de audio del iPhone",
  "conversation.volumeBalanceMicOn": "Audio a bordo con el micrófono activado",
  "conversation.volumeBalanceReply": "Volumen de la voz de la IA",
  "conversation.volumeBalanceHelp": "Baje el audio a bordo o suba la voz de la IA. Se guarda en este dispositivo.",
  "shell.onboard": "Cámara a bordo", "shell.emotion": "Mapa emocional del piloto y el vehículo",
  "shell.course": "Circuito de Suzuka y trazadas", "shell.conversation": "Conversación de voz con la IA del coche",
  "shell.telemetry": "Datos de conducción", "transport.controls": "Controles de reproducción",
  "shell.emotionTitle": "Mapa emocional", "shell.emotionDescription": "Piloto al fondo y vehículo en primer plano, superpuestos en un mapa",
  "shell.courseTitle": "Trazada real × trazada generada", "shell.courseDescription": "GPS registrado y trazada generada sin intervención en la misma vuelta y tiempo",
  "shell.conversationTitle": "Conversación de voz", "shell.conversationDescription": "Interfaz de voz controlada desde el logotipo de la IA",
  "shell.telemetryTitle": "Valores actuales × cronología", "shell.telemetryDescription": "Dirección, acelerador, freno, marcha, velocidad, RPM y respuesta del vehículo",
  "transport.play": "REPRODUCIR", "transport.pause": "PAUSA", "transport.car": "COCHE",
  "transport.mute": "Silenciar audio", "transport.unmute": "Activar audio",
  "transport.raceTime": "TIEMPO DE CARRERA", "transport.seek": "IR", "transport.seekLabel": "Posición desde la vuelta de formación hasta la meta",
  "story.title": "EVENTO / HISTORIA", "story.current": "Evento actual",
  "emotion.aria": "Estados emocionales independientes del piloto y del vehículo superpuestos en un mapa",
  "emotion.driver": "Piloto", "emotion.vehicle": "Vehículo", "emotion.sync": "Sincronía",
  "emotion.loadError": "No se pudo cargar el mapa emocional", "emotion.loading": "Cargando toda la cronología emocional…",
  "emotion.indicators": "Seis indicadores del piloto y del vehículo", "emotion.driverIndicators": "Indicadores del piloto",
  "emotion.vehicleIndicators": "Indicadores del vehículo", "emotion.syncRate": "Tasa de sincronía",
  "emotion.origin": "Situación: externa / Reacción: interna", "emotion.tooltipUnavailable": "Traducción no configurada",
  "emotion.axisPleasant": "Agradable", "emotion.axisUnpleasant": "Desagradable",
  "emotion.axisSituation": "Situación", "emotion.axisReaction": "Reacción",
  "emotion.cellsMandala": "celdas / mandala radial",
  "line.actualOnly": "Real", "line.generatedOnly": "Predicción", "line.both": "Todas", "line.loading": "Cargando",
  "line.actual": "Trazada real (GPS)", "line.predicted": "Trayectoria prevista", "line.driverIntent": "Intención del piloto",
  "line.vehicleResponse": "Respuesta prevista del vehículo", "line.toggle": "Visualización de trazadas",
  "line.aria": "Trazada GPS registrada, intención del piloto y respuesta prevista del vehículo",
  "telemetry.legend": "Leyenda", "telemetry.accelerator": "acelerador", "telemetry.brake": "freno",
  "telemetry.steering": "dirección", "telemetry.response": "respuesta", "telemetry.longG": "G longitudinal",
  "telemetry.latG": "G lateral", "telemetry.vertG": "G vertical", "telemetry.chart": "Datos de los últimos 10 segundos",
  "telemetry.current": "Operación y respuesta actuales", "network.live": "OBSERVACIÓN EN VIVO",
  "network.title": "Red de señales AzpNet", "network.raceView": "VISTA DE CARRERA", "network.loading": "cargando",
  "network.pulses": "pulsos reales", "network.nodes": "Nodos de red", "network.edges": "Enlaces de red",
  "status.shared": "Estado de reproducción compartido", "status.raceTime": "TIEMPO", "status.playState": "REPRODUCCIÓN",
  "status.selectedCar": "COCHE", "status.seekGeneration": "GENERACIÓN DE BÚSQUEDA",
  "status.playing": "EN REPRODUCCIÓN", "status.paused": "EN PAUSA",
  "error.raceLineApi": "La API de trazadas no respondió", "error.raceLineIdentity": "El coche o la generación de búsqueda de la trazada no coincide",
  "error.telemetryApi": "La API de datos de conducción no respondió", "error.conversationHttp": "La conversación de voz no está disponible",
  "error.videoSafeStopped": "no pudo completarse; el vídeo se detuvo de forma segura",
  "error.videoResynchronized": "no pudo completarse; la reproducción se resincronizó con el estado compartido anterior",
  "error.videoPrepare": "No se pudo preparar el vídeo", "error.videoClockMismatch": "El reloj compartido estaba en pausa mientras avanzaba el vídeo; el vídeo se detuvo de forma segura",
  "error.invalidRaceTime": "El tiempo de reproducción debe ser un número entero dentro del intervalo disponible", "error.videoAction": "No se pudo completar la operación de vídeo",
  "operation.initialSync": "Sincronización inicial del vídeo", "operation.seek": "Desplazamiento del vídeo", "operation.switchCar": "Cambio de cámara a bordo",
  "operation.play": "Reproducción del vídeo", "operation.pause": "Pausa del vídeo",
  "gauge.notReady": "No disponible", "gauge.recentSeries": "Últimos 2 segundos / serie real", "gauge.seriesNotReady": "Serie real no disponible",
  "gauge.mainContribution": "Contribución principal", "gauge.noEffect": "Sin efecto", "gauge.noContribution": "Sin datos de contribución generada",
  "gauge.reserve": "Reserva", "gauge.daAttenuation": "Atenuación DA", "gauge.naFastAttenuation": "Atenuación NA rápida",
  "gauge.indicators": "Indicadores seudohormonales / 0–1",
};

const FR: Catalog = {
  ...EN,
  "language.selector": "Langue d’affichage", "conversation.micOff": "Microphone désactivé",
  "conversation.waiting": "Prêt. Vous pouvez parler", "conversation.contextSync": "Synchronisation du contexte de course",
  "conversation.vadLoading": "Préparation de la détection vocale locale", "conversation.vadUnavailable": "La détection vocale est indisponible",
  "conversation.recognizing": "Écoute en cours", "conversation.transcribing": "Transcription de votre voix",
  "conversation.thinking": "L’IA de la voiture réfléchit", "conversation.preparing": "Préparation de la voix de l’IA",
  "conversation.responding": "L’IA de la voiture répond", "conversation.micOn": "MICRO OUVERT",
  "conversation.micOffLabel": "MICRO FERMÉ", "conversation.clickLogo": "Cliquez sur le logo",
  "conversation.turnMicOn": "Activer le microphone", "conversation.turnMicOff": "Désactiver le microphone",
  "conversation.venueNoise": "Trop de voix ambiantes sont détectées. Maintenez le logo pendant que vous parlez. La saisie normale revient quand le calme se prolonge.",
  "conversation.pushToTalk": "La voix est reconnue uniquement tant que vous maintenez le logo",
  "conversation.pushToTalkLabel": "APPUYER POUR PARLER", "conversation.holdLogo": "Maintenez le logo",
  "conversation.returnToNormalMic": "Revenir à la saisie vocale normale",
  "conversation.restartFailed": "Impossible de redémarrer le microphone", "conversation.unavailable": "La conversation est indisponible",
  "conversation.audioError": "La lecture vocale a échoué. Les sous-titres restent disponibles.",
  "conversation.playError": "Impossible de lire la réponse. Les sous-titres restent disponibles.",
  "conversation.mobileCaptionNote": "* Les sous-titres de la réponse s’affichent sur la vidéo embarquée ci-dessus.",
  "conversation.volumeBalance": "Balance audio de l’iPhone",
  "conversation.volumeBalanceMicOn": "Audio embarqué avec le micro activé",
  "conversation.volumeBalanceReply": "Volume de la voix de l’IA",
  "conversation.volumeBalanceHelp": "Baissez l’audio embarqué ou augmentez la voix de l’IA. Les réglages sont mémorisés.",
  "shell.onboard": "Caméra embarquée", "shell.emotion": "Carte émotionnelle du pilote et du véhicule",
  "shell.course": "Circuit de Suzuka et trajectoires", "shell.conversation": "Conversation vocale avec l’IA embarquée",
  "shell.telemetry": "Données de conduite", "transport.controls": "Commandes de lecture",
  "shell.emotionTitle": "Carte émotionnelle", "shell.emotionDescription": "Pilote en arrière-plan et véhicule au premier plan, superposés sur une carte",
  "shell.courseTitle": "Trajectoire réelle × trajectoire générée", "shell.courseDescription": "GPS enregistré et trajectoire générée sans intervention au même tour et au même instant",
  "shell.conversationTitle": "Conversation vocale", "shell.conversationDescription": "Interface vocale commandée depuis le logo de l’IA",
  "shell.telemetryTitle": "Valeurs actuelles × chronologie", "shell.telemetryDescription": "Direction, accélérateur, frein, rapport, vitesse, régime moteur et réponse du véhicule",
  "transport.play": "LECTURE", "transport.pause": "PAUSE", "transport.car": "VOITURE",
  "transport.mute": "Couper le son", "transport.unmute": "Rétablir le son",
  "transport.raceTime": "TEMPS DE COURSE", "transport.seek": "ALLER", "transport.seekLabel": "Position de la mise en grille à l’arrivée",
  "story.title": "ÉVÉNEMENT / RÉCIT", "story.current": "Événement actuel",
  "emotion.aria": "États émotionnels indépendants du pilote et du véhicule superposés sur une même carte",
  "emotion.driver": "Pilote", "emotion.vehicle": "Véhicule", "emotion.sync": "Synchronisation",
  "emotion.loadError": "Impossible de charger la carte émotionnelle", "emotion.loading": "Chargement de toute la chronologie émotionnelle…",
  "emotion.indicators": "Six indicateurs du pilote et du véhicule", "emotion.driverIndicators": "Indicateurs du pilote",
  "emotion.vehicleIndicators": "Indicateurs du véhicule", "emotion.syncRate": "Taux de synchronisation",
  "emotion.origin": "Situation : externe / Réaction : interne", "emotion.tooltipUnavailable": "Traduction non configurée",
  "emotion.axisPleasant": "Agréable", "emotion.axisUnpleasant": "Désagréable",
  "emotion.axisSituation": "Situation", "emotion.axisReaction": "Réaction",
  "emotion.cellsMandala": "cellules / mandala radial",
  "line.actualOnly": "Réelle", "line.generatedOnly": "Prévision", "line.both": "Toutes", "line.loading": "Chargement",
  "line.actual": "Trajectoire réelle (GPS)", "line.predicted": "Trajectoire prévue", "line.driverIntent": "Intention du pilote",
  "line.vehicleResponse": "Réponse prévue du véhicule", "line.toggle": "Affichage des trajectoires",
  "line.aria": "Trajectoire GPS enregistrée, intention du pilote et réponse prévue du véhicule",
  "telemetry.legend": "Légende", "telemetry.accelerator": "accélérateur", "telemetry.brake": "frein",
  "telemetry.steering": "direction", "telemetry.response": "réponse", "telemetry.longG": "G longitudinal",
  "telemetry.latG": "G latéral", "telemetry.vertG": "G vertical", "telemetry.chart": "Données des 10 dernières secondes",
  "telemetry.current": "Commande et réponse actuelles", "network.live": "OBSERVATION EN DIRECT",
  "network.title": "Réseau de signaux AzpNet", "network.raceView": "VUE COURSE", "network.loading": "chargement",
  "network.pulses": "impulsions réelles", "network.nodes": "Nœuds du réseau", "network.edges": "Liens du réseau",
  "status.shared": "État de lecture partagé", "status.raceTime": "TEMPS", "status.playState": "LECTURE",
  "status.selectedCar": "VOITURE", "status.seekGeneration": "GÉNÉRATION DE RECHERCHE",
  "status.playing": "LECTURE", "status.paused": "PAUSE",
  "error.raceLineApi": "L’API des trajectoires n’a pas répondu", "error.raceLineIdentity": "La voiture ou la génération de recherche de la trajectoire ne correspond pas",
  "error.telemetryApi": "L’API des données de conduite n’a pas répondu", "error.conversationHttp": "La conversation vocale est indisponible",
  "error.videoSafeStopped": "n’a pas pu être terminé ; la vidéo a été arrêtée en sécurité",
  "error.videoResynchronized": "n’a pas pu être terminé ; la lecture a été resynchronisée avec l’état partagé précédent",
  "error.videoPrepare": "Impossible de préparer la vidéo", "error.videoClockMismatch": "L’horloge partagée était en pause alors que la vidéo avançait ; la vidéo a été arrêtée en sécurité",
  "error.invalidRaceTime": "Le temps de lecture doit être un entier compris dans l’intervalle disponible", "error.videoAction": "Impossible de terminer l’opération vidéo",
  "operation.initialSync": "Synchronisation vidéo initiale", "operation.seek": "Déplacement dans la vidéo", "operation.switchCar": "Changement de caméra embarquée",
  "operation.play": "Lecture vidéo", "operation.pause": "Pause vidéo",
  "gauge.notReady": "Non disponible", "gauge.recentSeries": "2 dernières secondes / série réelle", "gauge.seriesNotReady": "Série réelle indisponible",
  "gauge.mainContribution": "Contribution principale", "gauge.noEffect": "Sans effet", "gauge.noContribution": "Aucune contribution générée",
  "gauge.reserve": "Réserve", "gauge.daAttenuation": "Atténuation DA", "gauge.naFastAttenuation": "Atténuation NA rapide",
  "gauge.indicators": "Indicateurs pseudo-hormonaux / 0–1",
};

const ZH: Catalog = {
  ...EN,
  "language.selector": "显示语言", "conversation.micOff": "麦克风已关闭", "conversation.waiting": "已就绪，请说话",
  "conversation.contextSync": "正在同步比赛信息", "conversation.vadLoading": "正在准备本地语音检测",
  "conversation.vadUnavailable": "语音检测不可用", "conversation.recognizing": "正在聆听",
  "conversation.transcribing": "正在识别语音", "conversation.thinking": "赛车AI正在思考",
  "conversation.preparing": "正在准备赛车AI语音", "conversation.responding": "赛车AI正在回答",
  "conversation.micOn": "麦克风 开", "conversation.micOffLabel": "麦克风 关", "conversation.clickLogo": "点击标志",
  "conversation.turnMicOn": "打开麦克风", "conversation.turnMicOff": "关闭麦克风",
  "conversation.venueNoise": "检测到过多周围人声。请按住标志说话。周围持续安静后会恢复普通输入。",
  "conversation.pushToTalk": "仅在按住标志时识别语音",
  "conversation.pushToTalkLabel": "按住说话", "conversation.holdLogo": "长按标志",
  "conversation.returnToNormalMic": "恢复普通语音输入",
  "conversation.restartFailed": "无法重新启动麦克风", "conversation.unavailable": "对话功能不可用",
  "conversation.audioError": "语音播放失败，字幕仍可使用。", "conversation.playError": "无法播放回答，字幕仍可使用。",
  "conversation.mobileCaptionNote": "※回答字幕显示在上方的车载视频中。",
  "conversation.volumeBalance": "iPhone 音量平衡",
  "conversation.volumeBalanceMicOn": "麦克风开启时的车载音量",
  "conversation.volumeBalanceReply": "AI 语音音量",
  "conversation.volumeBalanceHelp": "可降低车载音量或提高 AI 语音。设置保存在此设备上。",
  "shell.onboard": "车载摄像头", "shell.emotion": "车手与赛车情绪图", "shell.course": "铃鹿赛道与行驶线",
  "shell.conversation": "与赛车AI语音对话", "shell.telemetry": "行驶数据", "transport.controls": "比赛播放控制",
  "shell.emotionTitle": "情绪图", "shell.emotionDescription": "将车手置于背景、赛车置于前景并叠加在同一张图上",
  "shell.courseTitle": "实走线 × 生成线", "shell.courseDescription": "在相同圈次和时间显示记录GPS与非介入生成线",
  "shell.conversationTitle": "语音对话", "shell.conversationDescription": "通过AI标志操作的语音界面",
  "shell.telemetryTitle": "当前值 × 时间序列", "shell.telemetryDescription": "转向、油门、刹车、挡位、速度、转速与赛车响应",
  "transport.play": "播放", "transport.pause": "暂停", "transport.car": "赛车", "transport.raceTime": "比赛时间",
  "transport.mute": "静音", "transport.unmute": "恢复音频",
  "transport.seek": "跳转", "transport.seekLabel": "从暖胎圈到冲线的播放位置",
  "story.title": "比赛事件 / 故事", "story.current": "当前比赛事件",
  "emotion.aria": "将独立计算的车手与赛车情绪状态叠加在同一张图上",
  "emotion.driver": "车手", "emotion.vehicle": "赛车", "emotion.sync": "同步",
  "emotion.loadError": "无法加载情绪图", "emotion.loading": "正在加载完整情绪时间序列…",
  "emotion.indicators": "车手和赛车的六项指标", "emotion.driverIndicators": "车手指标",
  "emotion.vehicleIndicators": "赛车指标", "emotion.syncRate": "同步率",
  "emotion.origin": "情境：外部 / 反应：内部", "emotion.tooltipUnavailable": "未设置译文",
  "emotion.axisPleasant": "愉快", "emotion.axisUnpleasant": "不快",
  "emotion.axisSituation": "情境", "emotion.axisReaction": "反应",
  "emotion.cellsMandala": "单元 / 放射状曼荼罗",
  "line.actualOnly": "实走", "line.generatedOnly": "预测", "line.both": "全部", "line.loading": "加载中",
  "line.actual": "实走线（GPS）", "line.predicted": "预测轨迹", "line.driverIntent": "车手意图线",
  "line.vehicleResponse": "赛车响应预测", "line.toggle": "行驶线显示切换",
  "line.aria": "记录GPS实走线、车手意图与赛车响应预测",
  "telemetry.legend": "图例", "telemetry.accelerator": "油门", "telemetry.brake": "刹车",
  "telemetry.steering": "转向", "telemetry.response": "响应", "telemetry.longG": "纵向G",
  "telemetry.latG": "横向G", "telemetry.vertG": "垂直G", "telemetry.chart": "最近10秒行驶数据",
  "telemetry.current": "当前操作与响应", "network.live": "实时运行观测", "network.title": "AzpNet信号网络",
  "network.raceView": "比赛画面", "network.loading": "加载中", "network.pulses": "实际信号脉冲",
  "network.nodes": "网络节点", "network.edges": "网络连线", "status.shared": "共享播放状态",
  "status.raceTime": "比赛时间", "status.playState": "播放状态", "status.selectedCar": "所选赛车",
  "status.seekGeneration": "跳转代次",
  "status.playing": "播放中", "status.paused": "已暂停",
  "error.raceLineApi": "行驶线API未响应", "error.raceLineIdentity": "行驶线的赛车或跳转代次不一致",
  "error.telemetryApi": "行驶数据API未响应", "error.conversationHttp": "语音对话不可用",
  "error.videoSafeStopped": "未能完成；视频已安全停止", "error.videoResynchronized": "未能完成；播放已与上一共享状态重新同步",
  "error.videoPrepare": "无法准备视频", "error.videoClockMismatch": "共享时钟已暂停但视频仍在前进；视频已安全停止",
  "error.invalidRaceTime": "播放时间必须是可用范围内的整数", "error.videoAction": "无法完成视频操作",
  "operation.initialSync": "初始视频同步", "operation.seek": "视频跳转", "operation.switchCar": "切换车载镜头",
  "operation.play": "视频播放", "operation.pause": "视频暂停",
  "gauge.notReady": "未准备", "gauge.recentSeries": "最近2秒 / 实际序列", "gauge.seriesNotReady": "实际序列未准备",
  "gauge.mainContribution": "主要贡献", "gauge.noEffect": "无作用", "gauge.noContribution": "无生成侧贡献数据",
  "gauge.reserve": "储备量", "gauge.daAttenuation": "DA衰减", "gauge.naFastAttenuation": "NA快速衰减",
  "gauge.indicators": "伪激素指标 / 0–1",
};

export const TRANSLATIONS: Record<ConversationLanguage, Catalog> = {
  "ja-JP": JA, "en-US": EN, "es-ES": ES, "fr-FR": FR, "zh-CN": ZH,
};

export function isSupportedLanguage(value: unknown): value is ConversationLanguage {
  return CONVERSATION_LANGUAGES.some(item => item.locale === value);
}

export function chooseInitialLanguage(storage: Pick<Storage, "getItem"> | null, languages: readonly string[]): ConversationLanguage {
  const stored = storage?.getItem(APP_LANGUAGE_STORAGE_KEY);
  if (isSupportedLanguage(stored)) return stored;
  for (const language of languages) {
    const exact = CONVERSATION_LANGUAGES.find(item => item.locale.toLowerCase() === language.toLowerCase());
    if (exact) return exact.locale;
    const prefix = language.toLowerCase().split("-")[0];
    const match = CONVERSATION_LANGUAGES.find(item => item.htmlLang.toLowerCase().split("-")[0] === prefix);
    if (match) return match.locale;
  }
  return "ja-JP";
}

interface AppLanguageContextValue {
  language: ConversationLanguage;
  setLanguage: (language: ConversationLanguage) => void;
  t: (key: TranslationKey) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const AppLanguageContext = createContext<AppLanguageContextValue | null>(null);

export function AppLanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<ConversationLanguage>(() => chooseInitialLanguage(
    typeof window === "undefined" ? null : window.localStorage,
    typeof navigator === "undefined" ? [] : navigator.languages,
  ));
  const setLanguage = useCallback((next: ConversationLanguage) => setLanguageState(next), []);
  useEffect(() => {
    document.documentElement.lang = CONVERSATION_LANGUAGES.find(item => item.locale === language)?.htmlLang ?? "ja";
    window.localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, language);
  }, [language]);
  useEffect(() => {
    const receive = (event: StorageEvent) => {
      if (event.key === APP_LANGUAGE_STORAGE_KEY && isSupportedLanguage(event.newValue)) setLanguageState(event.newValue);
    };
    window.addEventListener("storage", receive);
    return () => window.removeEventListener("storage", receive);
  }, []);
  const t = useCallback((key: TranslationKey) => TRANSLATIONS[language][key], [language]);
  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat(language, options).format(value), [language]);
  const value = useMemo(() => ({ language, setLanguage, t, formatNumber }), [formatNumber, language, setLanguage, t]);
  return <AppLanguageContext.Provider value={value}>{children}</AppLanguageContext.Provider>;
}

export function useAppLanguage(): AppLanguageContextValue {
  const value = useContext(AppLanguageContext);
  if (!value) throw new Error("AppLanguageProvider is missing");
  return value;
}
